/**
 * Device ID utility for session management
 * Generates a unique device ID that persists across app sessions
 */

const DEVICE_ID_KEY = 'app_device_id';

/**
 * Generate a UUID v4
 */
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Generate a new device ID
 */
const generateDeviceId = (): string => {
  // Create a device ID with timestamp, random part, and UUID
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2);
  const uuid = generateUUID().replace(/-/g, '');

  return `${timestamp}-${randomPart}-${uuid}`.substring(0, 64);
};

/**
 * Get the current device ID, generate one if it doesn't exist
 */
export const getDeviceId = (): string => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);

  if (!deviceId) {
    deviceId = generateDeviceId();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
    console.log('Generated new device ID:', deviceId);
  }

  return deviceId;
};

/**
 * Reset device ID (force generate new one)
 * Useful for testing or when user wants to reset device identity
 */
export const resetDeviceId = (): string => {
  const newDeviceId = generateDeviceId();
  localStorage.setItem(DEVICE_ID_KEY, newDeviceId);
  console.log('Reset device ID to:', newDeviceId);
  return newDeviceId;
};

/**
 * Check if device ID exists
 */
export const hasDeviceId = (): boolean => {
  return !!localStorage.getItem(DEVICE_ID_KEY);
};
